import algoliasearch from 'algoliasearch';
import algoliasearchHelper from 'algoliasearch-helper';
import MicroModal from 'micromodal';
import aa from 'search-insights';

import getAlgoliaMarketingSearchIndexName from './searchModalIndex.util.js';

const {registerModule} = window;

const ALGOLIA_INSIGHTS_APP_ID = 'G0UEB6YYJ8';
const ALGOLIA_INSIGHTS_API_KEY = '064e94d836edc8ac8649f1c71468f264';

class SearchModal {
  constructor(element) {
    this.element = element;
    this.initialize();
  }

  bindEvents() {
    // The synthetic 'showmodal' event is dispatched by external elements — ex.,
    // navigationBar's search button.
    this.element.addEventListener('showmodal', this.handleShowModal.bind(this));

    this.ui.$close.addEventListener('click', this.handleClose.bind(this));
    this.ui.$input.addEventListener('input', this.handleInput.bind(this));
    this.ui.$modalContainer.addEventListener('click', this.handleContainerClick.bind(this));

    // Search events
    this.searchHelper.on('result', this.handleSearchResults.bind(this));
  }

  bindUI() {
    this.ui = {};
    this.ui.$close = this.element.querySelector('[data-ref="close"]');
    this.ui.$input = this.element.querySelector('[data-ref="input"]');
    this.ui.$modalContainer = this.element.querySelector('.modal__container');
    this.ui.$resultsList = this.element.querySelector('[data-ref="results-list"]');
    this.ui.$resultsPlaceholder = this.element.querySelector('[data-ref="results-placeholder"]');
    this.ui.$resultTemplate = this.element.querySelector('[data-ref="result-template"]');
    this.ui.$searchQuery = this.element.querySelector('[data-ref="search-query"]');
    this.ui.$results = this.element.querySelector('[data-ref="results"]');
  }

  /**
   * Maps content types to strings to allow renaming of the content type when displaying it. Prevents having to rename content types in Contentful potentially causing issues.
   *  @param {String} text
   * @return {String}
   */
  getPrettierName(text) {
    const map = {
      webinar: 'Webinars',
      trainingResource: 'Training Resources',
      trainingWorkflow: 'Training Resources',
    };
    return (text in map ? map[text] : text);
  }

  /**
   * Off /training/resources/, hash URIs point at modals on the listing page.
   * Downloadable (non-video) HTTPS URIs stay as-is so the gate can open them in a new tab,
   * matching behavior on /training/resources/. Video edge cases with HTTPS still use ?resource=.
   * @param {Object} result - Algolia hit
   * @param {string} permalink - result._uri
   * @return {string}
   */
  normalizeResultPermalink(result, permalink) {
    if (result.contentType === 'trainingWorkflow') {
      return permalink;
    }
    if (result.contentType !== 'trainingResource') {
      return permalink;
    }
    if (this.currentPagePath === '/training/resources/') {
      return permalink;
    }
    if (permalink.startsWith('#')) {
      return `/training/resources/${permalink}`;
    }
    if (
      !result.videoUrl &&
      /^https?:\/\//i.test(permalink)
    ) {
      return permalink;
    }
    if (/^https?:\/\//i.test(permalink)) {
      return `/training/resources/?resource=${encodeURIComponent(result.objectID)}`;
    }
    return permalink;
  }

  /**
   * @param {Object} result
   * @return {Object}
   */
  getResultMetadata(result) {
    let excerpt = '';

    const metadata = {
      category: result.category,
      title: result.title,
      permalink: result._uri,
      objectID: result.objectID,
    };

    // Posts and webinars
    // Need to add new content types here if you want them to show up
    if (
      result.contentType === 'post' ||
      result.contentType === 'webinar' ||
      result.contentType === 'trainingResource' ||
      result.contentType === 'trainingWorkflow'
    ) {
      excerpt = (result.thumbnailText || result.text || result.description || '').trim();
      metadata.resourceType = this.getPrettierName(result.type);
    }

    // Excerpt
    metadata.excerpt = excerpt.split(' ')
      .slice(0, 30)
      .filter((word) => word) // filter out empty strings
      .join(' ');

    if (excerpt.length > metadata.excerpt.length) {
      metadata.excerpt += '...';
    }

    if (result.marketoIdentifier && result.category === 'Downloadable Resource') {
      metadata.marketoIdentifier = result.marketoIdentifier;
    }

    return metadata;
  }

  handleClose() {
    MicroModal.close(this.modalId);
    this.resetModal();
  }

  handleContainerClick(event) {
    // Prevent clicks within the container from bubbling up, overriding
    // the default behavior which causes the modal to close.
    event.stopPropagation();
  }

  handleInput(event) {
    clearTimeout(this.debounceTimeout);
    this.searchQuery = event.target.value;

    if (this.searchQuery.length > 0) {
      this.debounceTimeout = setTimeout( async () => {
        // Perform the search after debouncing times out
        this.searchHelper.setQuery(this.searchQuery).search();
      }, 500 );
    } else {
      // Clear results if search query is empty
      this.searchResults = [];
      this.renderResults();
    }
  }

  handleSearchResults(event) {
    this.queryID = event.results?.queryID;
    this.searchResults = event.results?.hits || [];
    this.searchResults = this.searchResults.filter((result) => {
      const type = result.type.toLowerCase();
      if (this.types.includes(type)) {
        return result;
      }
    });
    this.renderResults();
  }

  handleShowModal() {
    MicroModal.show(this.modalId, {
      awaitCloseAnimation: true,
      disableScroll: true,
      onShow: () => {
        document.body.classList.add('modal-is-open');
      },
      onClose: () => {
        this.resetModal();
        document.body.classList.remove('modal-is-open');
      },
    });
  }

  initialize() {
    this.bindUI();
    this.setVars();
    this.bindEvents();
    MicroModal.init();
  }

  renderResults() {
    this.ui.$resultsList.replaceChildren();

    this.searchResults.forEach((result, index)=> {
      const element = this.ui.$resultTemplate.content.firstElementChild.cloneNode(true);

      // Populate template elements
      const meta = this.getResultMetadata(result);
      let {category, excerpt, objectID, permalink, resourceType, title} = meta;
      permalink = this.normalizeResultPermalink(result, permalink);

      // Fix for Training Resource modals
      try {
        if (this.currentPagePath === '/training/resources/') {
          element.addEventListener(
            'click',
            () => {
              this.handleClose();
            },
            {once: true},
          );
        }
      } catch (e) {
        console.error('Error: Browser does not support URL API');
      }

      const permalinkEl = element.querySelector('[data-ref="result-permalink"]');
      permalinkEl.setAttribute('data-search-hit-type', result.contentType || '');
      element.querySelector('[data-ref="result-excerpt"]').innerText = excerpt;
      permalinkEl.href = permalink;
      element.querySelector('[data-ref="result-title"]').textContent = title;
      element.querySelector('[data-ref="result-type"]').innerText = resourceType;

      const originalUri = result._uri || '';
      const isExternalHttpsTrainingResource =
        result.contentType === 'trainingResource' &&
        !result.videoUrl &&
        /^https?:\/\//i.test(originalUri);

      if (category) {
        element.querySelector('[data-ref="result-category"]').innerText = category;
      } else {
        element.querySelector('[data-ref="result-category"]').remove();
      }

      if (
        isExternalHttpsTrainingResource ||
        result.contentType === 'trainingWorkflow'
      ) {
        permalinkEl.target = '_blank';
        permalinkEl.rel = 'noopener noreferrer';
      }

      if (result.marketoIdentifier) {
        permalinkEl.setAttribute('data-marketo-identifier', result.marketoIdentifier);
      }

      permalinkEl.addEventListener('click', () => {
        aa('clickedObjectIDsAfterSearch', {
          eventName: 'Search Result Clicked',
          index: this.getIndex(),
          queryID: this.queryID,
          objectIDs: [objectID],
          positions: [index + 1],
        });
      });

      this.ui.$resultsList.appendChild(element);
    });

    this.ui.$searchQuery.innerText = this.searchQuery;

    this.ui.$resultsList.setAttribute('aria-hidden', this.searchQuery.length === 0 || this.searchResults.length === 0);
    this.ui.$resultsPlaceholder.setAttribute('aria-hidden', this.searchQuery.length === 0 || this.searchResults.length > 0);

    if (this.searchResults.length) {
      aa('viewedObjectIDs', {
        eventName: 'Search Hits Viewed',
        index: this.getIndex(),
        objectIDs: this.searchResults.map((r) => r.objectID),
        queryID: this.queryID,
      });
    }
  }

  resetModal() {
    // Reset query and results when the modal is closed
    this.ui.$input.value = '';
    this.searchQuery = '';
    this.searchResults = [];
    this.renderResults();
  }

  /**
   * Algolia index name. Delegates to shared helper; see `searchModalIndex.util.js` and indexer README (Algolia index names vs marketing search).
   * @return {string}
   */
  getIndex() {
    const searchEnvironment = this.element.dataset.searchEnvironment || 'production';
    return getAlgoliaMarketingSearchIndexName(searchEnvironment, this.lang);
  }

  setVars() {
    this.debounceTimeout = null;
    this.lang = document.documentElement.lang;
    this.modalId = this.element.dataset.modalId;
    this.scope = this.element.getAttribute('data-search-scope') || '';
    this.types = this.scope.split(',').map((t) => t.trim()).filter((t) => t.length > 0);
    aa('init', {
      appId: ALGOLIA_INSIGHTS_APP_ID,
      apiKey: ALGOLIA_INSIGHTS_API_KEY,
      useCookie: true,
    });
    this.searchClient = algoliasearch(
      'G0UEB6YYJ8',
      '195525021b740a37c597c6bcde232ffa',
    );
    this.searchHelper = algoliasearchHelper(
      this.searchClient,
      this.getIndex(),
      {clickAnalytics: true,
        disjunctiveFacets: ['type'],
        facets: ['excludeFromResourceCenter', 'excludeFromResourceHub'],
      },
    );
    this.types.forEach((type) => {
      this.searchHelper.addDisjunctiveFacetRefinement('type', type.toLowerCase());
    });
    this.searchHelper.addFacetExclusion('excludeFromResourceCenter', true);
    this.searchHelper.addFacetExclusion('excludeFromResourceHub', true);
    this.searchQuery = '';
    this.searchResults = [];
    this.currentPagePath = new URL(window.location.href).pathname;
  }
}

registerModule('SearchModal', SearchModal);
